import React, { useCallback, useEffect, useState } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { devicesApi, DeviceModel } from '../../../api/devicesApi';

interface DevicesCardProps {
  siteId?: number;
}

function formatMac(mac: string): string {
  const clean = mac.replace(/:/g, '').toUpperCase();
  return clean.match(/.{1,2}/g)?.join(':') ?? mac;
}

function StatusBadge({ status }: { status: DeviceModel['status'] }) {
  const colors: Record<DeviceModel['status'], string> = {
    unbound: '#8b8fa8',
    registered: '#6c6fff',
    bound: '#22c55e',
  };
  const labels: Record<DeviceModel['status'], string> = {
    unbound: 'не привязано',
    registered: 'зарегистрировано',
    bound: 'привязано к объекту',
  };
  return (
    <span style={{ color: colors[status], fontSize: 12, fontWeight: 600 }}>
      {labels[status]}
    </span>
  );
}

const DevicesCard: React.FC<DevicesCardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [devices, setDevices] = useState<DeviceModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bindBusy, setBindBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await devicesApi.getMyDevices();
      setDevices(data);
    } catch (_e: unknown) {
      setError('Ошибка загрузки устройств');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isCollapsed) {
      void load();
    }
  }, [isCollapsed, load]);


  const handleBind = async (device: DeviceModel) => {
    if (!siteId) return;
    setBindBusy(device.id);
    try {
      if (device.status === 'bound' && device.siteId === siteId) {
        await devicesApi.unbindDevice(device.id);
      } else {
        await devicesApi.bindDevice(device.id, siteId);
      }
      void load();
    } catch (_e: unknown) {
      // ignore
    } finally {
      setBindBusy(null);
    }
  };

  const listStyle: React.CSSProperties = {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.07))',
  };

  const macStyle: React.CSSProperties = {
    fontFamily: 'monospace',
    fontSize: 13,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const metaStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  };

  const siteLineStyle: React.CSSProperties = {
    fontSize: 11,
    color: 'var(--text-secondary, #8b8fa8)',
  };

  const bindBtnStyle = (isBound: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    fontSize: 12,
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    background: isBound ? 'rgba(239,68,68,0.15)' : 'rgba(108,111,255,0.18)',
    color: isBound ? '#f87171' : '#6c6fff',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  });


  return (
    <DashboardCard
      id="devices-card"
      title="Устройства"
      icon="ri-cpu-line"
      isCollapsible
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed((v) => !v)}
      headerActions={
        <span onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="card-action-btn"
            onClick={() => void load()}
            disabled={loading}
            title="Обновить"
          >
            <i className={loading ? 'ri-loader-4-line ri-spin' : 'ri-refresh-line'} />
          </button>
        </span>
      }
    >
      {loading && (
        <div style={{ color: 'var(--text-secondary, #8b8fa8)', fontSize: 13, padding: '8px 0' }}>
          Загрузка...
        </div>
      )}

      {!loading && error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0' }}>
          <span style={{ color: '#f87171', fontSize: 13 }}>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--accent, #6c6fff)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
          >
            Повторить
          </button>
        </div>
      )}

      {!loading && !error && devices.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary, #8b8fa8)', margin: '8px 0' }}>
          Нет зарегистрированных устройств. Зарегистрируйте устройство через мобильное приложение или введите секрет ниже.
        </p>
      )}

      {!loading && !error && devices.length > 0 && (
        <ul style={listStyle} aria-label="Список устройств">
          {devices.map((device) => {
            const isBoundToThisSite = device.status === 'bound' && device.siteId === siteId;
            const busy = bindBusy === device.id;
            return (
              <li key={device.id} style={itemStyle}>
                <div style={metaStyle}>
                  <span style={macStyle} title={device.mac}>
                    {formatMac(device.mac)}
                  </span>
                  <StatusBadge status={device.status} />
                  {device.siteId != null && (
                    <span style={siteLineStyle}>
                      Объект: {device.siteId}
                    </span>
                  )}
                </div>
                {siteId != null && (device.status === 'registered' || (device.status === 'bound')) && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void handleBind(device)}
                    style={bindBtnStyle(isBoundToThisSite)}
                  >
                    {busy ? '...' : isBoundToThisSite ? 'Отвязать' : 'Привязать'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

    </DashboardCard>
  );
};

export default DevicesCard;
